import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferDocumentFulfilmentModeEnumType,
  UnitTransferDocumentStatusEnumType,
  UnitTransferDocumentTypeEnumType,
  UnitTransferRequestStatusEnumType,
} from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import Badge from 'primitives/Badge';
import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import unitTransferDocumentFulfilmentModeToReadable from 'utils/enums/unit-transfer-document-fulfilment-mode-to-readable';
import unitTransferDocumentStatusToReadable from 'utils/enums/unit-transfer-document-status-to-readable';
import unitTransferDocumentTypeToReadable from 'utils/enums/unit-transfer-document-type-to-readable';

import AddChecklistItemDialogButton from './AddChecklistItemDialogButton';
import DocumentChecksList, { Check } from './DocumentChecksList';
import ReviewUnitTransferDocumentButtons from './ReviewUnitTransferDocumentButtons';
import UploadUnitTransferDocumentDialogButton from './UploadUnitTransferDocumentDialogButton';

const UNIT_TRANSFER_REQUEST_DOCUMENTS_QUERY = gql(`
  query getUnitTransferRequestDocuments($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      status
      documentChecklistProgress {
        approvedCount
        fulfilledCount
        requiredCount
      }
      documents {
        id
        type
        name
        status
        fulfilmentMode
        isRequired
        url
        rejectionReason
        reviewedAt
        createdAt
        checks {
          id
          checkKey
          label
          severity
          aiVerdict
          humanVerdict
          humanOverrideNote
          confidence
          evidence
          extractedValue
        }
      }
    }
  }
`);

const REMOVE_UNIT_TRANSFER_CHECKLIST_ITEM_MUTATION = gql(`
  mutation RemoveUnitTransferChecklistItem($documentId: ID!) {
    removeUnitTransferChecklistItem(documentId: $documentId) {
      id
      documents {
        id
      }
    }
  }
`);

const MARK_UNIT_TRANSFER_DOCUMENTS_COMPLETE_MUTATION = gql(`
  mutation MarkUnitTransferDocumentsComplete($requestId: ID!) {
    markUnitTransferDocumentsComplete(requestId: $requestId) {
      id
      status
      documentsVerifiedAt
    }
  }
`);

const UPLOADABLE_REQUEST_STATUSES = [
  UnitTransferRequestStatusEnumType.ConditionsIssued,
  UnitTransferRequestStatusEnumType.PendingDocuments,
  UnitTransferRequestStatusEnumType.DocumentsIncomplete,
];

// Mirrors the backend: investors submit once their OWN documents are in, so an admin-uploaded item
// is usually still missing at that point and ops supplies it during review. Without
// DocumentsUnderReview here the upload button would disappear at exactly the moment ops needs it.
const ADMIN_UPLOADABLE_REQUEST_STATUSES = [
  ...UPLOADABLE_REQUEST_STATUSES,
  UnitTransferRequestStatusEnumType.DocumentsUnderReview,
];

const CHECKLIST_MANAGEABLE_REQUEST_STATUSES = [
  UnitTransferRequestStatusEnumType.ConditionsIssued,
  UnitTransferRequestStatusEnumType.PendingDocuments,
  UnitTransferRequestStatusEnumType.DocumentsUnderReview,
  UnitTransferRequestStatusEnumType.DocumentsIncomplete,
];

const REVIEWABLE_DOCUMENT_STATUSES = [
  UnitTransferDocumentStatusEnumType.Uploaded,
  UnitTransferDocumentStatusEnumType.AiChecked,
];

type DocumentRow = {
  id: string;
  type: UnitTransferDocumentTypeEnumType;
  status: UnitTransferDocumentStatusEnumType;
  fulfilmentMode: UnitTransferDocumentFulfilmentModeEnumType;
  isRequired: boolean;
  url?: string | null;
  rejectionReason?: string | null;
  checks: Check[];
};

// PendingUpload reads differently depending on who owes the file — an investor hasn't acted yet,
// but an admin-upload or signature row sitting at PendingUpload is someone else's open task.
const getDocumentStatusBadge = (document: DocumentRow): { label: string; color: string } => {
  if (document.status === UnitTransferDocumentStatusEnumType.PendingUpload) {
    if (document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.Signature)
      return { label: 'Awaiting e-signature', color: 'gray' };
    if (document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.AdminUpload)
      return { label: 'Awaiting your upload', color: 'amber' };
  }
  return unitTransferDocumentStatusToReadable(document.status);
};

const countOutstandingBlockChecks = (document: DocumentRow): number =>
  document.checks.filter(
    check =>
      check.severity === 'BLOCK' &&
      (check.humanVerdict === null || check.humanVerdict === undefined)
  ).length;

const RemoveChecklistItemButton: FC<{ documentId: string }> = ({ documentId }) => {
  const [removeChecklistItem, { loading, error }] = useMutation(
    REMOVE_UNIT_TRANSFER_CHECKLIST_ITEM_MUTATION,
    {
      refetchQueries: ['getUnitTransferRequestDocuments'],
    }
  );

  return (
    <ConfirmButton
      variant="secondary"
      loading={loading}
      error={error}
      description="Remove this document from the checklist? It will no longer be required."
      onConfirm={() => removeChecklistItem({ variables: { documentId } })}
    >
      Remove
    </ConfirmButton>
  );
};

// One row per document in the side list — the status badge and outstanding-check count are what
// let ops tell which documents still need attention without opening each one.
const DocumentNavItem: FC<{ document: DocumentRow; isSelected: boolean; onSelect: () => void }> = ({
  document,
  isSelected,
  onSelect,
}) => {
  const { label, color } = getDocumentStatusBadge(document);
  const outstandingChecks = countOutstandingBlockChecks(document);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex-shrink-0 text-left px-3 py-2.5 rounded-md border transition-colors md:flex-shrink ${
        isSelected
          ? 'bg-indigo-50 border-indigo-200'
          : 'bg-white border-transparent hover:bg-gray-50'
      }`}
    >
      <p className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
        {unitTransferDocumentTypeToReadable(document.type)}
        {!document.isRequired && <span className="font-normal text-gray-400"> (optional)</span>}
      </p>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Badge label={label} color={color} showIndicator />
        {outstandingChecks > 0 && (
          <span className="text-xs font-medium text-red-600">{outstandingChecks} to review</span>
        )}
      </div>
    </button>
  );
};

const DocumentDetailPanel: FC<{
  document: DocumentRow;
  requestId: string;
  canAdminUpload: boolean;
  canManageChecklist: boolean;
  canReview: boolean;
}> = ({ document, requestId, canAdminUpload, canManageChecklist, canReview }) => {
  const { label, color } = getDocumentStatusBadge(document);

  return (
    <SectionCard
      title={`${unitTransferDocumentTypeToReadable(document.type)}${
        document.isRequired ? '' : ' (optional)'
      }`}
      subTitle={unitTransferDocumentFulfilmentModeToReadable(document.fulfilmentMode)}
      headerContent={<Badge label={label} color={color} showIndicator />}
      bodyClassName="px-4 py-3 sm:p-4 flex flex-col gap-3"
    >
      <>
        {document.rejectionReason && (
          <p className="text-sm text-red-600">Rejected: {document.rejectionReason}</p>
        )}
        {document.url && (
          <a
            className="w-fit text-sm text-blue-500 hover:underline"
            href={document.url}
            target="_blank"
            rel="noreferrer"
          >
            View File
          </a>
        )}

        {document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.Signature ? (
          <p className="text-sm text-gray-500">
            Fulfilled via signature — no upload required here.
          </p>
        ) : (
          <>
            {document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.AdminUpload &&
              canAdminUpload && (
                <UploadUnitTransferDocumentDialogButton
                  requestId={requestId}
                  type={document.type}
                  label={unitTransferDocumentTypeToReadable(document.type)}
                />
              )}
            <DocumentChecksList
              documentId={document.id}
              documentUrl={document.url}
              checks={document.checks}
            />
          </>
        )}

        <div className="flex flex-row gap-2">
          {canReview && REVIEWABLE_DOCUMENT_STATUSES.includes(document.status) && (
            <ReviewUnitTransferDocumentButtons documentId={document.id} />
          )}
          {canManageChecklist &&
            document.status === UnitTransferDocumentStatusEnumType.PendingUpload && (
              <RemoveChecklistItemButton documentId={document.id} />
            )}
        </div>
      </>
    </SectionCard>
  );
};

const UnitTransferDocuments: FC = () => {
  const { unitTransferId } = useParams<{ unitTransferId: string }>() as { unitTransferId: string };
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const { loading, error, data, refetch } = useQuery(UNIT_TRANSFER_REQUEST_DOCUMENTS_QUERY, {
    variables: { id: unitTransferId },
  });

  const [markDocumentsComplete, { loading: markingComplete, error: markCompleteError }] =
    useMutation(MARK_UNIT_TRANSFER_DOCUMENTS_COMPLETE_MUTATION);

  if (loading && !data) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const unitTransferRequest = data.unitTransferRequest;
  const { documentChecklistProgress: progress } = unitTransferRequest;
  const documents = unitTransferRequest.documents.filter(
    doc => doc.status !== UnitTransferDocumentStatusEnumType.Superseded
  );

  const activeTypes = documents.map(doc => doc.type);
  const availableTypes = Object.values(UnitTransferDocumentTypeEnumType).filter(
    type => !activeTypes.includes(type)
  );

  const canManageChecklist = CHECKLIST_MANAGEABLE_REQUEST_STATUSES.includes(
    unitTransferRequest.status
  );
  const canAdminUpload = ADMIN_UPLOADABLE_REQUEST_STATUSES.includes(unitTransferRequest.status);
  const canReview =
    unitTransferRequest.status === UnitTransferRequestStatusEnumType.DocumentsUnderReview;

  const selectedDocument =
    documents.find(doc => doc.id === selectedDocumentId) ?? documents[0] ?? null;

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-row items-center justify-between">
        <p className="text-sm text-gray-600">
          {progress.approvedCount} of {progress.requiredCount} required documents approved (
          {progress.fulfilledCount} fulfilled)
        </p>
        <div className="flex gap-2">
          {canManageChecklist && (
            <AddChecklistItemDialogButton
              requestId={unitTransferId}
              availableTypes={availableTypes}
            />
          )}
          {canReview && (
            <ConfirmButton
              loading={markingComplete}
              error={markCompleteError}
              description="Mark all documents complete? This verifies the document phase and moves the request to signing."
              onConfirm={() => markDocumentsComplete({ variables: { requestId: unitTransferId } })}
            >
              Mark Documents Complete
            </ConfirmButton>
          )}
        </div>
      </div>

      {documents.length === 0 ? (
        <p className="text-sm text-gray-600">No documents on the checklist yet.</p>
      ) : (
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex flex-row gap-2 overflow-x-auto md:w-72 md:flex-shrink-0 md:flex-col md:overflow-visible">
            {documents.map(document => (
              <DocumentNavItem
                key={document.id}
                document={document}
                isSelected={selectedDocument?.id === document.id}
                onSelect={() => setSelectedDocumentId(document.id)}
              />
            ))}
          </div>

          <div className="min-w-0 flex-1">
            {selectedDocument && (
              <DocumentDetailPanel
                document={selectedDocument}
                requestId={unitTransferId}
                canAdminUpload={canAdminUpload}
                canManageChecklist={canManageChecklist}
                canReview={canReview}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitTransferDocuments;
